import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import { setInitialLoadTotal, setInitialLoadProgress, setInitialLoadTotalOnly } from '../store/authSlice';
import { setRoles, setAssignablePermissions } from '../store/rolesSlice';
import { setUsers, setUserRoles } from '../store/usersSlice';
import { setCustomFields } from '../store/customFieldsSlice';
import { setEmployeeTags } from '../store/employeeTagsSlice';
import { setShifts } from '../store/shiftsSlice';
import { setShiftGroups } from '../store/shiftGroupsSlice';
import { setItems as setShiftDistributions } from '../store/shiftDistributionsSlice';
import { syncEmployees } from '../store/employeesSlice';
import { setSettings, setFieldLists, setGlobalSettings, setUIFields } from '../store/applicationSettingsSlice';
import { setLeaveTypes } from '../store/leaveTypesSlice';
import { setLeavePolicies } from '../store/leavePoliciesSlice';
import { setPublicHolidays } from '../store/publicHolidaysSlice';
import type { FieldSetting } from '../types/applicationSettings';
import { apiService } from '../services/api';
import { getFrontendCacheUUID, setFrontendCacheUUID, clearEmployeeDB } from '../services/employeeDB';

export function useInitialLoad(): void {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const sessionStatus = useAppSelector((s) => s.auth.sessionStatus);
  const selectedUnit = useAppSelector((s) => s.auth.selectedUnit);

  useEffect(() => {
    if (!isAuthenticated || sessionStatus !== 'Allowed') return;

    // Start with 3 steps: cache check + bootstrap + employee sync.
    // The employee sync will update the total once it knows how many chunks it needs.
    dispatch(setInitialLoadTotal(3));

    let cancelled = false;

    (async () => {
      // Step 1: Frontend cache UUID check — must run before employee sync.
      const dbKey = selectedUnit
        ? `cranberry-employees-${selectedUnit.tenant}-${selectedUnit.solution}-${selectedUnit.unit}`
        : 'cranberry-employees';
      const storedUUID = getFrontendCacheUUID(dbKey);
      const cacheResponse = await apiService.getFrontendCacheId(storedUUID);
      const returnedUUID = cacheResponse.data.uuid;
      if (!cancelled && returnedUUID !== storedUUID) {
        setFrontendCacheUUID(dbKey, returnedUUID);
        await clearEmployeeDB(dbKey);
      }
      if (!cancelled) dispatch(setInitialLoadProgress(1));

      // Step 2: Bootstrap — load all non-employee app data.
      if (cancelled) return;
      const response = await apiService.getBootstrap();
      const data = response.data;
      if (data.roles !== undefined) dispatch(setRoles(data.roles));
      if (data.permissions !== undefined) dispatch(setAssignablePermissions(data.permissions));
      if (data.users !== undefined) {
        dispatch(setUsers(data.users));
        const rolesMap: Record<string, string[]> = {};
        data.users.forEach((u) => { rolesMap[u.username] = u.roles ?? []; });
        dispatch(setUserRoles(rolesMap));
      }
      if (data.customFields !== undefined) dispatch(setCustomFields(data.customFields));
      if (data.employeeTags !== undefined) dispatch(setEmployeeTags(data.employeeTags));
      if (data.shifts !== undefined) dispatch(setShifts(data.shifts));
      if (data.shiftGroups !== undefined) dispatch(setShiftGroups(data.shiftGroups));
      if (data.shiftDistributions !== undefined) dispatch(setShiftDistributions(data.shiftDistributions));
      if (data.applicationSettings !== undefined) {
        dispatch(setSettings(data.applicationSettings));
        const fieldLists: Record<string, FieldSetting[]> = {};
        for (const s of data.applicationSettings) {
          try {
            const blob = s.settingJsonString ? JSON.parse(s.settingJsonString) : {};
            fieldLists[s.settingId] = blob.fieldSettingList ?? [];
          } catch {
            fieldLists[s.settingId] = [];
          }
        }
        dispatch(setFieldLists(fieldLists));
      }
      if (data.globalSettings !== undefined) dispatch(setGlobalSettings(data.globalSettings));
      if (data.uiFields !== undefined) dispatch(setUIFields(data.uiFields));
      if (data.leaveTypes !== undefined) dispatch(setLeaveTypes(data.leaveTypes));
      if (data.leavePolicies !== undefined) dispatch(setLeavePolicies(data.leavePolicies));
      if (data.publicHolidays !== undefined) dispatch(setPublicHolidays(data.publicHolidays));

      if (!cancelled) dispatch(setInitialLoadProgress(2));

      // Step 3+: Employee sync — chunks progress reported via callbacks.
      // onTotalDiscovered: called once with the number of steps inside the sync
      //   (1 UUID GET + n chunk POSTs). We expand the total accordingly.
      // onStepComplete: called after each sync step (1-indexed within the sync).
      if (cancelled) return;
      await dispatch(syncEmployees({
        onTotalDiscovered: (syncTotal) => {
          if (!cancelled) dispatch(setInitialLoadTotalOnly(2 + syncTotal));
        },
        onStepComplete: (syncCompleted) => {
          if (!cancelled) dispatch(setInitialLoadProgress(2 + syncCompleted));
        },
      }));
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, sessionStatus, selectedUnit, dispatch]);
}
